import React from "react";
import { useDispatch } from "react-redux";
import { checkout, ctaText } from "rtr-constants";
import { bagTabs, switchBagTab, toggleBag } from "actions/bag-actions";
import CEBagActions from "actions/ce-bag-actions";
import RtrButton from "components/design-system/rtr-button";
import { useAppSelector } from "../../../../../../util/store-types";

type Address = {
  postalCode: string;
  default: boolean;
};

type Sku = {
  canonicalSize: string[];
};

type SelectedSize = {
  sku: Sku;
};

type StaticProduct = {
  productId: string;
  msrp: number;
};

export type BuyAddToBagButtonProps = {
  addresses: Address[];
  selectedSize: SelectedSize | null;
  styleUnavailable: boolean;
  selectSizeIsUnavailable?: boolean;
  staticProduct: StaticProduct;
};

const BuyAddToBagButton: React.FC<BuyAddToBagButtonProps> = ({
  addresses,
  selectedSize,
  styleUnavailable,
  selectSizeIsUnavailable,
  staticProduct,
}) => {
  const [addingToBag, setAddingToBag] = React.useState(false);
  const isBagLoading = (useAppSelector(state => state?.ceBagLoading) && addingToBag) || false;
  const dispatch = useDispatch();
  const disabled = styleUnavailable || selectSizeIsUnavailable || !selectedSize || isBagLoading;

  const buyAddToBag = () => {
    setAddingToBag(true);
    const defaultAddress = addresses?.find(address => address?.default === true);

    const loggingData = {
      object_type: "kif",
      pSize: selectedSize?.sku?.canonicalSize?.[0],
      styleName: staticProduct?.productId,
      membership: null,
      membership_type: null,
      membership_id: null,
      productId: staticProduct?.productId,
      retailPriceInteger: staticProduct?.msrp,
    };

    const handleAddToBagSuccess = () => {
      dispatch(toggleBag(true));
      dispatch(switchBagTab(bagTabs.CLASSIC_TAB));
      dispatch(CEBagActions.get());
      setAddingToBag(false);
    };

    const handleAddToBagFailure = () => {
      setAddingToBag(false);
    };

    const addBuyItemToBag = CEBagActions.add(
      {
        sku: selectedSize?.sku,
        type: checkout.orderItemTypes.KIF_FROM_RACK,
        zipCode: defaultAddress?.postalCode,
        product: staticProduct,
      },
      false,
      loggingData,
      handleAddToBagSuccess,
      handleAddToBagFailure
    );

    dispatch(addBuyItemToBag);
  };

  const buttonText = !selectedSize
    ? ctaText.CHOOSE_SIZE
    : isBagLoading
    ? ctaText.ADDING
    : disabled
    ? ctaText.UNAVAILABLE
    : ctaText.ADD_TO_BAG;

  return (
    <RtrButton
      style="primary"
      className="mt-8 mb-4"
      dataTestId="updp-buy-atb"
      dataHeapId="updp-buy-atb"
      loading={isBagLoading}
      disabled={disabled}
      onClick={buyAddToBag}
      fullWidth>
      {buttonText}
    </RtrButton>
  );
};

export default BuyAddToBagButton;
